import { useState, useEffect } from "react";
import { Space, Button, Row, Col, Input, Drawer, Select, Form, notification } from "antd";
import {
  useCreateLocationMutation,
  useGetLocationQuery,
  useGetLocationsQuery,
  useUpdateLocationMutation
} from "../../redux/services/locationsApi";
import ErrorDetails from "../../components/ErrorDetails";

const { Option } = Select;

const LocationSidebar = (props: any) => {
  const { isOpen, setOpen, locationId, refetch } = props;

  const [api, contextHolder] = notification.useNotification();

  const [form] = Form.useForm();
  const [locationType, setLocationType] = useState("");

  const { data: locationData, isLoading } = useGetLocationQuery(locationId);
  const selectedLocationData: any = locationData?.location;

  const [createLocation, { isLoading: createInProgress }] = useCreateLocationMutation();

  const [updateLocation, { isLoading: updateInProgress }] = useUpdateLocationMutation();

  const { data: stateLocationsData } = useGetLocationsQuery({ all_pages: true, location_type: "state" });
  const states = stateLocationsData?.locations;

  const { data: zoneLocationsData } = useGetLocationsQuery({ all_pages: true, location_type: "zone" });
  const zones = zoneLocationsData?.locations;


  useEffect(() => {
    if (selectedLocationData) {
      form.setFieldsValue({
        ...selectedLocationData,
        location_type: selectedLocationData?.location_type,
        parent_id: selectedLocationData?.parent?.id
      });
      setLocationType(selectedLocationData?.location_type);
    }
    return () => {
      form.resetFields();
    };
  }, [selectedLocationData]);

  const onFinish = (values: any) => {
    if (locationId) {
      onUpdateLocation(values);
    } else {
      addLocation(values);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const handleLocationTypeChange = (value: string) => {
    form.setFieldsValue({ location_type: value });
    setLocationType(value);
  };

  const addLocation = async (values: object) => {
    try {
      await createLocation(values).unwrap();
      api.success({
        message: "Create location operation successful",
        placement: "top"
      });
      form.resetFields();
      setOpen(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: "Create location operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
    }
  };

  const onUpdateLocation = async (values: object) => {
    try {
      await updateLocation({ id: locationId, location: values }).unwrap();
      api.success({
        message: "Update location operation successful",
        placement: "top"
      });
      form.resetFields();
      setOpen(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: "Update location operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
    }
  };

  return (
    <>
      {contextHolder}
      <Drawer
        title={`${locationId ? "Update" : "Add"} Location`}
        placement="right"
        closable={true}
        onClose={() => {
          form.resetFields();
          setOpen(!isOpen);
        }}
        open={isOpen}
        key={"location-drawer"}
      >
        <Form
          name="location_form"
          form={form}
          initialValues={{ remember: true }}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          disabled={updateInProgress || createInProgress || isLoading}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true, message: "Please input name" },
              { min: 3, message: "Name must be minimum 3 characters." },
              { max: 30, message: "Name must be maximum 30 characters." }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Code"
            name="code"
            rules={[
              { required: true, message: "Please input code" },
              { min: 2, message: "Code must be minimum 2 characters." },
              { max: 5, message: "Code must be maximum 5 characters." }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="location_type"
            label="Location Type"
            rules={[{ required: true, message: "Please select location type" }]}
          >
            <Select
              placeholder="Select location type"
              allowClear
              onSelect={handleLocationTypeChange}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              <Option value="zone">Zone</Option>
              <Option value="state">State</Option>
              <Option value="city">City</Option>
            </Select>
          </Form.Item>

          {(locationType === "state" || locationType === "city") && (
            <Form.Item
              label="Parent"
              name="parent_id"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const locationType = getFieldValue("location_type");
                    if ((locationType === "state" || locationType === "city") && !value) {
                      return Promise.reject("Please select parent");
                    }
                    return Promise.resolve();
                  }
                })
              ]}
            >
              <Select
                placeholder="Select parent"
                allowClear
                showSearch
                optionFilterProp="children"
                filterOption={(input, option: any) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {locationType === "state" &&
                  zones?.map((loc: any) => (
                    <Option key={loc.name} value={loc.id}>
                      {loc.name}
                    </Option>
                  ))}

                {locationType === "city" &&
                  states?.map((loc: any) => (
                    <Option key={loc.name} value={loc.id}>
                      {loc.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select
              placeholder="Select status"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="disabled">Disabled</Option>
            </Select>
          </Form.Item>

          <Form.Item wrapperCol={{ offset: 0, span: 24 }}>
            <Row>
              <Col>
                <Space size={"middle"}>
                  <Button
                    type="default"
                    onClick={() => {
                      form.resetFields();
                      setOpen(!isOpen);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={updateInProgress || createInProgress}
                  >
                    {locationId ? "Update" : "Add"}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

export default LocationSidebar;
